import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, User } from 'lucide-react';
import type { UserProfile } from '@/types/userAdmin';

interface UserListProps {
  users: UserProfile[];
  selectedUserId: string | null;
  onSelectUser: (userId: string) => void;
  loading?: boolean;
}

/**
 * UserList component displays a searchable list of users
 * with ability to select a user to view details
 * 
 * @param {UserListProps} props - Component properties
 * @returns {JSX.Element} Rendered user list component
 */
export function UserList({ users, selectedUserId, onSelectUser, loading = false }: UserListProps) {
  const { t } = useTranslation('auth');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }

    const query = searchQuery.toLowerCase();
    return users.filter(user => {
      const username = user.username?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      return username.includes(query) || email.includes(query);
    });
  }, [users, searchQuery]);

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Check if user is disabled (has a deleted_at timestamp)
  const isUserDisabled = (user: UserProfile) => !!user.deleted_at;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="relative">
          <div className="h-10 bg-neutral-200 animate-pulse rounded-lg" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-20 bg-neutral-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={20} />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('userAdmin.search')}
          className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          aria-label="Search users by name or email"
          role="searchbox"
        />
      </div>

      {/* User List */}
      <div className="space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            <User className="mx-auto mb-2 text-neutral-400" size={48} />
            <p>{t('userAdmin.noResults')}</p>
          </div>
        ) : (
          filteredUsers.map(user => (
            <button
              key={user.id}
              onClick={() => onSelectUser(user.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectUser(user.id);
                }
              }}
              className={`
                w-full text-left p-4 rounded-lg border transition-all
                ${selectedUserId === user.id
                  ? 'bg-primary-50 border-primary-500 shadow-md'
                  : 'bg-white border-neutral-200 hover:border-primary-300 hover:shadow-sm'
                }
                ${isUserDisabled(user) ? 'opacity-60' : ''}
              `}
              aria-label={`View profile for ${user.username || user.email || 'user'}`}
              aria-pressed={selectedUserId === user.id}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-neutral-900 truncate">
                      {user.username || 'No username'}
                    </p>
                    {isUserDisabled(user) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        {t('userAdmin.profile.disabled')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600 truncate">
                    {user.email}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Joined {formatDate(user.created_at)}
                  </p>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
